<template>
  <div class="hgm-panel" :class="{ 'hgm-panel--has-selection': !!selected }">

    <!-- ══════════════════════════════════════════════════════════
         LISTA DE CONVERSACIONES
    ══════════════════════════════════════════════════════════ -->
    <section class="hgm-list">

      <!-- Header -->
      <div class="hgm-list__header">
        <div class="hgm-list__title-row">
          <h2 class="hgm-list__title">🏠 Mensajes Host-Huésped</h2>
          <span class="hgm-badge hgm-badge--count">{{ conversations.length }}</span>
        </div>

        <!-- Buscador -->
        <div class="hgm-search-wrap">
          <span class="hgm-search-icon">🔍</span>
          <input
            v-model="searchQuery"
            class="hgm-search"
            type="text"
            placeholder="Buscar conversación…"
            @input="onSearch"
          />
          <button v-if="searchQuery" class="hgm-search-clear" @click="clearSearch">✕</button>
        </div>
      </div>

      <!-- Estado: cargando -->
      <div v-if="loading" class="hgm-state">
        <span class="hgm-spinner"></span>
        <span>Cargando…</span>
      </div>

      <!-- Estado: error -->
      <div v-else-if="error" class="hgm-state hgm-state--error">
        <span>⚠️ {{ error }}</span>
        <button class="hgm-btn-retry" @click="fetchConversations">Reintentar</button>
      </div>

      <!-- Estado: vacío -->
      <div v-else-if="conversations.length === 0" class="hgm-state">
        Sin conversaciones.
      </div>

      <!-- Items -->
      <div v-else class="hgm-list__items">
        <div
          v-for="c in conversations"
          :key="c.id"
          class="hgm-conv"
          :class="{ 'hgm-conv--selected': selectedId === c.id }"
          @click="selectConversation(c)"
        >
          <!-- Avatar alojamiento -->
          <div class="hgm-conv__avatar">
            <img
              v-if="c.accommodation && c.accommodation.photos && c.accommodation.photos[0]"
              :src="c.accommodation.photos[0]"
              :alt="c.accommodation ? localeName(c.accommodation.title) : ''"
              class="hgm-conv__avatar-img"
            />
            <span v-else>🏠</span>
          </div>

          <!-- Cuerpo -->
          <div class="hgm-conv__body">
            <div class="hgm-conv__row1">
              <span class="hgm-conv__title">
                {{ c.accommodation ? localeName(c.accommodation.title) : c.title || 'Sin alojamiento' }}
              </span>
              <span class="hgm-conv__time">{{ relativeTime(c.last_message_at) }}</span>
            </div>

            <!-- Participantes -->
            <div class="hgm-conv__participants">
              <span class="hgm-role hgm-role--host">🏠 {{ c.host_name }}</span>
              <span class="hgm-role-sep">·</span>
              <span class="hgm-role hgm-role--guest">👤 {{ c.guest_name }}</span>
            </div>

            <!-- Preview último mensaje -->
            <div class="hgm-conv__preview">{{ c.last_message || 'Sin mensajes aún' }}</div>
          </div>
        </div>

        <!-- Paginación -->
        <div v-if="meta && meta.current_page < meta.last_page" class="hgm-paginator">
          <button class="hgm-btn-more" :disabled="loadingMore" @click="loadMore">
            <span v-if="loadingMore" class="hgm-spinner hgm-spinner--sm"></span>
            <span v-else>Cargar más</span>
          </button>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════════════
         PANEL DE MENSAJES
    ══════════════════════════════════════════════════════════ -->
    <section class="hgm-chat">

      <!-- Empty state -->
      <div v-if="!selected" class="hgm-chat__empty">
        <span>💬</span>
        <p>Seleccioná una conversación para ver los mensajes</p>
      </div>

      <template v-else>

        <!-- Header -->
        <div class="hgm-chat__header">
          <button type="button" class="hgm-chat-back" title="Volver" @click="mobileBack">⟨</button>
          <div class="hgm-chat__header-left">
            <div class="hgm-chat__avatar">
              <img
                v-if="selected.accommodation && selected.accommodation.photos && selected.accommodation.photos[0]"
                :src="selected.accommodation.photos[0]"
                class="hgm-chat__avatar-img"
              />
              <span v-else>🏠</span>
            </div>
            <div>
              <div class="hgm-chat__name">
                {{ selected.accommodation ? localeName(selected.accommodation.title) : selected.title }}
              </div>
              <div class="hgm-chat__sub">
                <span class="hgm-role hgm-role--host">🏠 {{ selected.host_name }}</span>
                <span class="hgm-role-sep">→</span>
                <span class="hgm-role hgm-role--guest">👤 {{ selected.guest_name }}</span>
              </div>
            </div>
          </div>

          <!-- Sin acciones de soporte en este panel (solo lectura del equipo) -->
          <div class="hgm-chat__header-tag">
            <span class="hgm-badge hgm-badge--info">Solo lectura</span>
          </div>
        </div>

        <!-- Info bar -->
        <div class="hgm-info-bar">
          <!-- Alojamiento -->
          <div class="hgm-info-block hgm-info-block--property">
            <span class="hgm-info-label">Alojamiento</span>
            <a
              v-if="selected.accommodation"
              class="hgm-prop-link"
              :href="accommodationUrl(selected.accommodation)"
              target="_blank"
              rel="noopener noreferrer"
              :title="'Abrir ' + localeName(selected.accommodation.title)"
            >
              <svg class="hgm-prop-link__pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span class="hgm-prop-link__name">{{ localeName(selected.accommodation.title) }}</span>
              <svg class="hgm-prop-link__go" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </a>
            <span v-else class="hgm-party__name">—</span>
          </div>

          <!-- Host -->
          <div class="hgm-info-block hgm-party hgm-party--host">
            <span class="hgm-info-label">Host</span>
            <span class="hgm-party__name">{{ selected.host_name || '—' }}</span>
            <div v-if="selected.host_phone || selected.host_email" class="hgm-party__contacts">
              <a v-if="selected.host_phone" class="hgm-contact" :href="'tel:' + selected.host_phone">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                <span class="hgm-contact__text">{{ selected.host_phone }}</span>
              </a>
              <a v-if="selected.host_email" class="hgm-contact" :href="'mailto:' + selected.host_email" :title="selected.host_email">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg>
                <span class="hgm-contact__text">{{ selected.host_email }}</span>
              </a>
            </div>
          </div>

          <!-- Huésped -->
          <div class="hgm-info-block hgm-party hgm-party--guest">
            <span class="hgm-info-label">Huésped</span>
            <span class="hgm-party__name">{{ selected.guest_name || '—' }}</span>
            <div v-if="selected.guest_phone || selected.guest_email" class="hgm-party__contacts">
              <a v-if="selected.guest_phone" class="hgm-contact" :href="'tel:' + selected.guest_phone">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                <span class="hgm-contact__text">{{ selected.guest_phone }}</span>
              </a>
              <a v-if="selected.guest_email" class="hgm-contact" :href="'mailto:' + selected.guest_email" :title="selected.guest_email">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg>
                <span class="hgm-contact__text">{{ selected.guest_email }}</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Mensajes -->
        <div v-if="loadingMessages" class="hgm-state">
          <span class="hgm-spinner"></span>
          <span>Cargando mensajes…</span>
        </div>

        <div v-else-if="messages.length === 0" class="hgm-state">
          Sin mensajes en esta conversación.
        </div>

        <div v-else ref="messagesEl" class="hgm-messages">
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="hgm-msg"
            :class="msg.sender_role === 'guest' ? 'hgm-msg--guest' : 'hgm-msg--host'"
          >
            <div class="hgm-msg__wrap">
              <div class="hgm-msg__sender">
                {{ msg.sender_role === 'guest' ? '👤 ' + msg.guest_name : '🏠 ' + msg.host_name }}
              </div>
              <div class="hgm-msg__bubble">
                <!-- Media preview -->
                <template v-if="msg.media_url">
                  <img
                    v-if="isImage(msg)"
                    :src="msg.media_url"
                    class="hgm-msg__media hgm-msg__media--image"
                    alt="Adjunto"
                    @click="openMedia(msg)"
                  />

                  <video v-else-if="isVideo(msg)" :src="msg.media_url" class="hgm-msg__media hgm-msg__media--video" controls preload="metadata"></video>

                  <AudioMessage v-else-if="isAudio(msg)" :src="msg.media_url" :avatar-url="msg.sender_avatar || msg.avatar || msg.sender_photo || ''" :msg="msg" class="hgm-msg__media hgm-msg__media--audio" />

                  <a v-else-if="isDocument(msg)" :href="msg.media_url" target="_blank" rel="noopener noreferrer" class="hgm-msg__doc">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path></svg>
                    <span class="hgm-msg__doc-name">{{ getFileName(msg.media_url) }}</span>
                  </a>

                  <img v-else-if="isSticker(msg)" :src="msg.media_url" class="hgm-msg__media hgm-msg__media--sticker" alt="Sticker" @click="openMedia(msg)" />

                  <a v-else :href="msg.media_url" target="_blank" rel="noopener noreferrer" class="hgm-msg__doc">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path></svg>
                    <span class="hgm-msg__doc-name">{{ getFileName(msg.media_url) }}</span>
                  </a>
                </template>

                <p v-if="msg.message" class="hgm-msg__body">{{ msg.message }}</p>
                <span class="hgm-msg__time">{{ formatTime(msg.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Nota de solo lectura (los agentes de soporte no escriben acá) -->
        <div class="hgm-readonly-bar">
          🔒 Esta conversación es entre el host y el huésped. El equipo de soporte solo puede leerla.
        </div>

      </template>
    </section>

  </div>
</template>

<script>
import apiUrls from '@/new-constants/apiUrls'
import { localeText as localeTextUtil } from '@/Utils/Utils'
import AudioMessage from './AudioMessage.vue'
const BASE_URL = apiUrls.base

// Debounce simple sin lodash
function debounce(fn, delay) {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => fn.apply(this, args), delay)
  }
}

export default {
  name: 'HostGuestMessages',
  components: { AudioMessage },

  data() {
    return {
      // Lista
      conversations:  [],
      meta:           null,
      loading:        false,
      loadingMore:    false,
      error:          null,
      searchQuery:    '',
      currentPage:    1,

      // Detalle
      selected:       null,
      selectedId:     null,
      messages:       [],
      loadingMessages: false,
    }
  },

  created() {
    this.debouncedSearch = debounce(this.fetchConversations, 300)
    this.fetchConversations()
  },

  methods: {

    // ── Fetch conversaciones ────────────────────────────────────

    async fetchConversations(resetPage = true) {
      if (resetPage) this.currentPage = 1
      this.loading = true
      this.error   = null

      try {
        const params = new URLSearchParams({
          page:  this.currentPage,
          limit: 50,
        })
        if (this.searchQuery.trim()) params.set('q', this.searchQuery.trim())

        const token = localStorage.getItem('token') || ''
        const res  = await fetch(`${BASE_URL}host-guest/conversations?${params}`, { headers: { 'Accept': 'application/json', 'Authorization': token } })
        const json = await res.json()

        this.conversations = (json && json.data) ? json.data : []
        this.meta          = (json && json.meta) ? json.meta : null
      } catch {
        this.error = 'No se pudo conectar con el servidor.'
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      if (this.loadingMore) return
      this.loadingMore = true
      this.currentPage++

      try {
        const params = new URLSearchParams({
          page:  this.currentPage,
          limit: 50,
        })
        if (this.searchQuery.trim()) params.set('q', this.searchQuery.trim())

        const token = localStorage.getItem('token') || ''
        const res  = await fetch(`${BASE_URL}host-guest/conversations?${params}`, { headers: { 'Accept': 'application/json', 'Authorization': token } })
        const json = await res.json()

        this.conversations = [...this.conversations, ...((json && json.data) ? json.data : [])]
        this.meta          = (json && json.meta) ? json.meta : null
      } catch {
        // silencioso; el botón vuelve a habilitarse
        this.currentPage--
      } finally {
        this.loadingMore = false
      }
    },

    // ── Fetch mensajes ──────────────────────────────────────────

    async selectConversation(c) {
      this.selected        = c
      this.selectedId      = c.id
      this.messages        = []
      this.loadingMessages = true

      try {
        const token = localStorage.getItem('token') || ''
        const res  = await fetch(`${BASE_URL}host-guest/messages/${c.id}`, { headers: { 'Accept': 'application/json', 'Authorization': token } })
        const json = await res.json()
        const raw = (json && json.data) ? json.data : []
        this.messages = raw.map(m => Object.assign({}, m, this.normalizeMediaFields ? this.normalizeMediaFields(m) : {}))
        this.$nextTick(this.scrollToBottom)
      } catch {
        this.messages = []
      } finally {
        this.loadingMessages = false
      }
    },

    // Volver a la lista en móvil (no altera datos, solo la vista)
    mobileBack() {
      this.selected = null
      this.selectedId = null
      this.messages = []
    },

    // ── Búsqueda ────────────────────────────────────────────────

    onSearch() {
      this.debouncedSearch(true)
    },

    clearSearch() {
      this.searchQuery = ''
      this.fetchConversations(true)
    },

    // ── Helpers ──────────────────────────────────────────────────

    scrollToBottom() {
      const el = this.$refs.messagesEl
      if (el) el.scrollTop = el.scrollHeight
    },

    relativeTime(iso) {
      if (!iso) return ''
      const diff = Date.now() - new Date(iso).getTime()
      const m = Math.floor(diff / 60000)
      if (m < 1)  return 'ahora'
      if (m < 60) return `${m}m`
      const h = Math.floor(m / 60)
      if (h < 24) return `${h}h`
      return `${Math.floor(h / 24)}d`
    },

    formatTime(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
    },
    // Media helpers
    isImage(msg) {
      if (!msg || !msg.media_url) return false
      try {
        const ext = String(msg.media_url).split('?')[0].split('.').pop().toLowerCase()
        if (/^(png|jpe?g|gif|webp|svg)$/.test(ext)) return true
      } catch (e) { /* ignore */ }
      if (msg.media_type) return msg.media_type === 'image'
      return false
    },
    isVideo(msg) {
      if (!msg || !msg.media_url) return false
      try {
        const ext = String(msg.media_url).split('?')[0].split('.').pop().toLowerCase()
        if (/^(mp4|webm|ogg|mov)$/.test(ext)) return true
      } catch (e) { /* ignore */ }
      if (msg.media_type) return msg.media_type === 'video'
      return false
    },
    isAudio(msg) {
      if (!msg || !msg.media_url) return false
      try {
        const ext = String(msg.media_url).split('?')[0].split('.').pop().toLowerCase()
        if (/^(mp3|wav|ogg|m4a)$/.test(ext)) return true
      } catch (e) { /* ignore */ }
      if (msg.media_type) return msg.media_type === 'audio'
      return false
    },
    isDocument(msg) {
      if (!msg || !msg.media_url) return false
      try {
        const ext = String(msg.media_url).split('?')[0].split('.').pop().toLowerCase()
        if (/^(pdf|docx?|xlsx?|pptx?|txt|csv|rtf|zip|rar|7z|tar|gz)$/.test(ext)) return true
      } catch (e) { /* ignore */ }
      if (msg.media_type) return msg.media_type === 'document'
      return false
    },
    isSticker(msg) {
      if (!msg || !msg.media_url) return false
      if (msg.media_type) return msg.media_type === 'sticker'
      try {
        const ext = String(msg.media_url).split('?')[0].split('.').pop().toLowerCase()
        return /^(webp|png|gif)$/.test(ext)
      } catch (e) { return false }
    },
    normalizeMediaFields(msg) {
      if (!msg) return {}
      let media_url = msg.media_url || msg.url || (msg.media && msg.media.url) || msg.audio || msg.file_url || msg.attachment_url || (msg.file && msg.file.url) || (msg.attach && msg.attach.url) || ''
      let media_type = msg.media_type || msg.type || (msg.media && msg.media.type) || (msg.mime && String(msg.mime).startsWith('audio') ? 'audio' : null) || null
      if (media_url && !media_type) {
        try {
          const ext = String(media_url).split('?')[0].split('.').pop().toLowerCase()
          if (/^(mp3|wav|ogg|m4a)$/.test(ext)) media_type = 'audio'
          else if (/^(mp4|webm|ogg|mov)$/.test(ext)) media_type = 'video'
          else if (/^(png|jpe?g|gif|webp|svg)$/.test(ext)) media_type = 'image'
          else if (/^(pdf|docx?|xlsx?|pptx?|txt|csv|rtf|zip|rar|7z|tar|gz)$/.test(ext)) media_type = 'document'
        } catch (e) { /* ignore */ }
      }
      const out = {}
      if (media_url) out.media_url = media_url
      if (media_type) out.media_type = media_type
      return out
    },
    getFileName(url) {
      if (!url) return ''
      try {
        const raw = String(url).split('/').pop().split('?')[0]
        const name = decodeURIComponent(raw)
        return name.length > 40 ? name.slice(0, 36) + '...' : name
      } catch (e) { return String(url).slice(0, 40) }
    },
    openMedia(msg) { if (!msg || !msg.media_url) return; window.open(msg.media_url, '_blank') },
    accommodationUrl(acc) {
      if (!acc || !acc.id) return '#'
      return this.$router.resolve({ name: 'detalle-alojamiento', params: { idalojamiento: acc.id } }).href
    },
    localeName(v) { return localeTextUtil(v, (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'es') }
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');
/* ─── TOKENS ────────────────────────────────────── */
.hgm-panel {
  --primary:       #FC7010;
  --primary-light: rgba(252,112,16,0.10);
  --primary-dim:   rgba(252,112,16,0.22);
  --host-color:    #B91E7C;
  --host-light:    #FFF1E5;
  --host-border:   #FFD4AE;
  --guest-color:   #1d4ed8;
  --guest-light:   #eff6ff;
  --guest-border:  #bfdbfe;
  --bg-base:       #F4F3F0;
  --bg-list:       #FAFAF9;
  --bg-chat:       #ffffff;
  --bg-surface:    #F4F3F0;
  --bg-hover:      #ffffff;
  --border:        #ECEAE4;
  --border-strong: #DBD9D2;
  --text:          #0A0A0B;
  --text-muted:    #A0A0A8;
  --text-sub:      #6E6E76;
  --r:    10px;
  --r-sm:  7px;
  --list-w: 300px;

    display: grid;
    grid-template-columns: var(--list-w) 1fr;
    /* Forzar altura completa de viewport para que los hijos con flex:1
      calculen correctamente y puedan hacer overflow/scroll. Esto evita
      dependencias en estilos globales (html/body height). */
    height: 100vh;
    max-height: 100vh;
    min-height: 0;
    overflow: hidden;
  background: var(--bg-base);
  color: var(--text);
  font-family: 'Geist', 'DM Sans', 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
}

/* ─── LISTA ─────────────────────────────────────── */
.hgm-list {
  background: var(--bg-list);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.hgm-list__header {
  padding: 14px 14px 10px;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--bg-list);
  position: sticky;
  top: 0;
  z-index: 2;
}

.hgm-list__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hgm-list__title {
  font-size: 13px;
  font-weight: 800;
  margin: 0;
  color: var(--text);
  flex: 1;
  letter-spacing: -0.2px;
}

/* Buscador */
.hgm-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.hgm-search-icon {
  position: absolute;
  left: 9px;
  font-size: 11px;
  pointer-events: none;
  color: var(--text-muted);
}
.hgm-search {
  width: 100%;
  background: #ffffff;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  color: var(--text);
  font-size: 12.5px;
  font-family: inherit;
  padding: 8px 28px 8px 28px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;
}
.hgm-search::placeholder { color: var(--text-muted); }
.hgm-search:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(252,112,16,0.12);
}
.hgm-search-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 12px;
  padding: 2px;
  line-height: 1;
  transition: color 0.15s;
}
.hgm-search-clear:hover { color: var(--text-sub); }

/* Items */
.hgm-list__items { flex: 1; overflow-y: auto; min-height: 0; }

.hgm-conv {
  display: flex;
  gap: 10px;
  padding: 11px 14px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.12s ease;
  position: relative;
}
.hgm-conv:hover {
  background: var(--bg-hover);
  box-shadow: 0 1px 6px rgba(0,0,0,0.05);
  transform: translateX(1px);
}
.hgm-conv--selected {
  background: var(--primary-light);
  box-shadow: inset 3px 0 0 var(--primary);
}

.hgm-conv__avatar {
  width: 42px;
  height: 42px;
  border-radius: var(--r-sm);
  background: #F4F3F0;
  border: 1px solid var(--border-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  overflow: hidden;
}
.hgm-conv__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Media preview styles for host-guest panel */
.hgm-msg__media { display:block; max-width:360px; max-height:480px; border-radius:10px; margin-bottom:6px; cursor:pointer; }
.hgm-msg__media--image { width:auto; height:auto; max-width:360px; object-fit:cover; }
.hgm-msg__media--video { width:100%; max-width:360px; border-radius:8px; background:#000; }
.hgm-msg__media--audio { width:260px; }
.hgm-msg__media--sticker { width:160px; height:160px; object-fit:contain; background:transparent; border-radius:8px; }
.hgm-msg__doc { display:inline-flex; align-items:center; gap:8px; padding:8px 10px; border:1px solid var(--border); border-radius:8px; text-decoration:none; color:inherit; max-width:360px; }
.hgm-msg__doc svg { opacity:0.85; }
.hgm-msg__doc-name { font-size:13px; color:var(--text-sub); }

.hgm-conv__body { flex: 1; min-width: 0; }

.hgm-conv__row1 {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 3px;
}
.hgm-conv__title {
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hgm-conv__time {
  font-size: 11px;
  color: var(--text-muted);
  flex-shrink: 0;
  margin-left: 6px;
}

.hgm-conv__participants {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
}

.hgm-conv__preview {
  font-size: 11.5px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Roles */
.hgm-role {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 20px;
}
.hgm-role--host {
  color: var(--host-color);
  background: var(--host-light);
}
.hgm-role--guest {
  color: var(--guest-color);
  background: var(--guest-light);
}
.hgm-role-sep {
  color: var(--text-muted);
  font-size: 11px;
}

/* Paginador */
.hgm-paginator {
  padding: 12px;
  text-align: center;
}
.hgm-btn-more {
  background: #ffffff;
  border: 1px solid var(--border-strong);
  color: var(--text-sub);
  padding: 7px 18px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}
.hgm-btn-more:hover:not(:disabled) {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-light);
}
.hgm-btn-more:disabled { opacity: 0.4; cursor: not-allowed; }

/* ─── PANEL DE CHAT ──────────────────────────────── */
.hgm-chat {
  background: var(--bg-chat);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.hgm-chat__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  gap: 10px;
  font-size: 14px;
}
.hgm-chat__empty span {
  font-size: 40px;
  opacity: 0.2;
}
.hgm-chat__empty p {
  color: var(--text-muted);
  font-size: 13px;
}

/* Header */
.hgm-chat__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--border);
  background: #ffffff;
}
.hgm-chat__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.hgm-chat__avatar {
  width: 44px;
  height: 44px;
  border-radius: var(--r-sm);
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  overflow: hidden;
}
.hgm-chat__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hgm-chat__name {
  font-size: 14px;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.2px;
}
.hgm-chat__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}
.hgm-chat__header-tag { flex-shrink: 0; }

/* Info bar */
.hgm-info-bar {
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--border);
  background: #FAFAF9;
}
.hgm-info-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  padding: 12px 20px;
  border-right: 1px solid var(--border);
  flex: 1;
  min-width: 210px;
}
.hgm-info-block:last-child { border-right: none; }
.hgm-info-label {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--text-muted);
  text-transform: uppercase;
  margin-bottom: 0;
}
.hgm-info-value {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-sub);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Party (host / huésped) identity */
.hgm-party .hgm-info-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.hgm-party .hgm-info-label::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}
.hgm-party--host  .hgm-info-label::before { background: var(--host-color); }
.hgm-party--guest .hgm-info-label::before { background: var(--guest-color); }

.hgm-party__name {
  font-size: 15.5px;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.hgm-party__contacts {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 8px;
}
.hgm-contact {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--text-sub);
  text-decoration: none;
  max-width: 100%;
  transition: color 0.15s ease;
}
.hgm-contact svg {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  opacity: 0.6;
  transition: opacity 0.15s ease;
}
.hgm-contact__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hgm-party--host  .hgm-contact:hover { color: var(--host-color); }
.hgm-party--guest .hgm-contact:hover { color: var(--guest-color); }
.hgm-contact:hover svg { opacity: 1; }
.hgm-contact:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: 3px;
}

/* Alojamiento → link pill */
.hgm-info-block--property { flex: 1.3; }
.hgm-prop-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  align-self: flex-start;
  max-width: 100%;
  padding: 5px 8px 5px 9px;
  border: 1px solid var(--primary-dim);
  background: var(--primary-light);
  color: var(--primary);
  border-radius: var(--r-sm);
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.12s ease;
}
.hgm-prop-link:hover {
  background: var(--primary);
  border-color: var(--primary);
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(252,112,16,0.28);
}
.hgm-prop-link:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.hgm-prop-link__pin { width: 14px; height: 14px; flex-shrink: 0; }
.hgm-prop-link__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hgm-prop-link__go { width: 14px; height: 14px; flex-shrink: 0; opacity: 0.65; margin-left: 2px; }
.hgm-prop-link:hover .hgm-prop-link__go { opacity: 1; }

/* Mensajes */
.hgm-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #FAFAF9;
}

.hgm-msg { display: flex; }
.hgm-msg--guest { justify-content: flex-end; }
.hgm-msg--host  { justify-content: flex-start; }

.hgm-msg__wrap {
  display: flex;
  flex-direction: column;
  max-width: 68%;
  gap: 3px;
  animation: hgm-msg-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes hgm-msg-in {
  from { opacity: 0; transform: translateY(6px) scale(0.97); }
  to   { opacity: 1; transform: none; }
}
.hgm-msg--guest .hgm-msg__wrap { align-items: flex-end; }

.hgm-msg__sender {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0 4px;
}
.hgm-msg--host  .hgm-msg__sender { color: var(--host-color); }
.hgm-msg--guest .hgm-msg__sender { color: var(--guest-color); }

.hgm-msg__bubble {
  padding: 10px 13px;
  border-radius: var(--r);
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.hgm-msg--host .hgm-msg__bubble {
  background: var(--host-light);
  border: 1px solid var(--host-border);
  border-bottom-left-radius: 3px;
  color: var(--text);
}
.hgm-msg--guest .hgm-msg__bubble {
  background: var(--guest-light);
  border: 1px solid var(--guest-border);
  border-bottom-right-radius: 3px;
  color: var(--text);
}

.hgm-msg__media {
  max-width: 220px;
  border-radius: var(--r-sm);
  margin-bottom: 6px;
  display: block;
  border: 1px solid var(--border);
}

.hgm-msg__body {
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
.hgm-msg__time {
  display: block;
  font-size: 10px;
  color: var(--text-muted);
  margin-top: 5px;
  text-align: right;
}

/* Barra de solo lectura */
.hgm-readonly-bar {
  border-top: 1px solid var(--border);
  background: #FAFAF9;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
  padding: 10px 18px;
  text-align: center;
}

/* ─── BADGES ────────────────────────────────────── */
.hgm-badge {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
}
.hgm-badge--count {
  background: #F4F3F0;
  border: 1px solid var(--border-strong);
  color: var(--text-sub);
}
.hgm-badge--info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

/* ─── ESTADOS / SPINNER ─────────────────────────── */
.hgm-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-muted);
  padding: 24px;
  font-size: 13px;
}
.hgm-state--error { color: #dc2626; }

.hgm-spinner {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2.5px solid #ECEAE4;
  border-top-color: var(--primary);
  animation: hgm-spin 0.75s linear infinite;
  display: inline-block;
}
.hgm-spinner--sm { width: 14px; height: 14px; border-width: 2px; }
@keyframes hgm-spin { to { transform: rotate(360deg); } }

.hgm-btn-retry {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 5px 12px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: all 0.15s;
}
.hgm-btn-retry:hover { background: #fee2e2; }

/* Scrollbar */
* {
  scrollbar-width: thin;
  scrollbar-color: #ECEAE4 transparent;
}

/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — móvil y tablet
════════════════════════════════════════════════════════════════ */

/* Botón volver (solo móvil) */
.hgm-chat-back {
  display: none;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: #ffffff;
  color: var(--host-color);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  margin-right: 8px;
}
.hgm-chat-back:active { transform: scale(0.94); }

/* ─── TABLET (≤ 1024px) ─── */
@media (max-width: 1024px) {
  .hgm-panel {
    --list-w: 260px;
    height: 100%;
    max-height: 100%;
  }
  /* Sitio para la hamburguesa del dashboard en las cabeceras */
  .hgm-list__header { padding-left: 52px; }
}

/* ─── MÓVIL (≤ 640px): una sola columna, chat deslizante ─── */
@media (max-width: 640px) {
  .hgm-panel {
    grid-template-columns: 1fr;
    position: relative;
  }
  /* Chat a pantalla completa: fixed + 100dvh para ocupar todo el alto real */
  .hgm-chat {
    position: fixed;
    inset: 0;
    z-index: 1000;
    height: 100vh;
    height: 100dvh;
    background: var(--bg-chat);
    transform: translateX(100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .hgm-panel--has-selection .hgm-chat { transform: translateX(0); }

  /* Mostrar el botón volver y compactar la cabecera del chat */
  .hgm-chat-back { display: inline-flex; }
  .hgm-chat__header { flex-wrap: wrap; }

  /* La info-bar (alojamiento / host / huésped) se apila */
  .hgm-info-bar { flex-direction: column; align-items: stretch; }
}
</style>